import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  TrendingUp, TrendingDown, Package, AlertTriangle, 
  ShoppingCart, BarChart3, PieChart 
} from 'lucide-react';

const COLORS = ['#059669', '#0891b2', '#7c3aed', '#db2777', '#ea580c', '#ca8a04', '#64748b'];

function BarChartSimple({ data, dataKeyA, dataKeyB, labelKey, height = 200 }) {
  if (!data || data.length === 0) return <p className="text-gray-400 text-sm text-center py-8">Aucune donnee</p>;
  const maxVal = Math.max(...data.map(d => Math.max(d[dataKeyA] || 0, d[dataKeyB] || 0)), 1);

  return (
    <div className="flex items-end gap-1.5 justify-between" style={{ height }} data-testid="bar-chart">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
          <div className="flex items-end gap-0.5 w-full justify-center" style={{ height: height - 24 }}>
            <div
              className="bg-emerald-500 rounded-t-sm w-3 min-h-[2px] transition-all duration-500"
              style={{ height: `${((d[dataKeyA] || 0) / maxVal) * 100}%` }}
              title={`${dataKeyA}: ${d[dataKeyA] || 0}`}
            />
            <div
              className="bg-cyan-500 rounded-t-sm w-3 min-h-[2px] transition-all duration-500"
              style={{ height: `${((d[dataKeyB] || 0) / maxVal) * 100}%` }}
              title={`${dataKeyB}: ${d[dataKeyB] || 0}`}
            />
          </div>
          <span className="text-[10px] text-gray-500 truncate max-w-[48px]">{d[labelKey]}</span>
        </div>
      ))}
    </div>
  );
}

function DonutChart({ data, valueKey, labelKey, size = 160 }) {
  if (!data || data.length === 0) return <p className="text-gray-400 text-sm text-center py-8">Aucune donnee</p>;
  const total = data.reduce((sum, d) => sum + (d[valueKey] || 0), 0);
  if (total === 0) return <p className="text-gray-400 text-sm text-center py-8">Aucune donnee</p>;
  const r = size / 2 - 10;
  const cx = size / 2;
  const cy = size / 2;
  let cumulAngle = -90;

  return (
    <div className="flex items-center gap-4" data-testid="donut-chart">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {data.map((d, i) => {
          const pct = (d[valueKey] || 0) / total;
          const angle = pct * 360;
          const startAngle = cumulAngle;
          cumulAngle += angle;
          const endAngle = cumulAngle;
          const largeArc = angle > 180 ? 1 : 0;
          const startRad = (startAngle * Math.PI) / 180;
          const endRad = (endAngle * Math.PI) / 180;
          const x1 = cx + r * Math.cos(startRad);
          const y1 = cy + r * Math.sin(startRad);
          const x2 = cx + r * Math.cos(endRad);
          const y2 = cy + r * Math.sin(endRad);
          const pathD = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
          return <path key={i} d={pathD} fill={COLORS[i % COLORS.length]} opacity={0.85} />;
        })}
        <circle cx={cx} cy={cy} r={r * 0.55} fill="white" />
        <text x={cx} y={cy - 6} textAnchor="middle" className="text-lg font-bold fill-gray-800">{total.toFixed(0)}</text>
        <text x={cx} y={cy + 12} textAnchor="middle" className="text-[10px] fill-gray-500">EUR</text>
      </svg>
      <div className="flex flex-col gap-1">
        {data.slice(0, 5).map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
            <span className="text-gray-700 capitalize truncate max-w-[100px]">{d[labelKey]}</span>
            <span className="text-gray-500 ml-auto">{(d[valueKey] || 0).toFixed(0)} EUR</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HorizontalBars({ data, nameKey, valueKey, maxItems = 10, unitKey, thresholdKey }) {
  if (!data || data.length === 0) return <p className="text-gray-400 text-sm text-center py-8">Aucune donnee</p>;
  const items = data.slice(0, maxItems);
  const maxVal = Math.max(...items.map(d => d[valueKey] || 0), 1);

  return (
    <div className="space-y-2" data-testid="stock-bars">
      {items.map((d, i) => {
        const pct = ((d[valueKey] || 0) / maxVal) * 100;
        const isLow = thresholdKey && d[thresholdKey] > 0 && d[valueKey] <= d[thresholdKey];
        return (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs text-gray-700 w-24 truncate" title={d[nameKey]}>{d[nameKey]}</span>
            <div className="flex-1 bg-gray-100 rounded-full h-4 relative overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${isLow ? 'bg-red-400' : 'bg-emerald-400'}`}
                style={{ width: `${Math.max(pct, 2)}%` }}
              />
              {thresholdKey && d[thresholdKey] > 0 && (
                <div
                  className="absolute top-0 h-full w-px bg-amber-600"
                  style={{ left: `${Math.min((d[thresholdKey] / maxVal) * 100, 100)}%` }}
                  title={`Seuil: ${d[thresholdKey]}`}
                />
              )}
            </div>
            <span className="text-xs text-gray-500 w-16 text-right">{d[valueKey]} {d[unitKey] || ''}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/analytics/dashboard');
      setAnalytics(res.data);
    } catch {
      // Silently handle - empty state shown
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (!analytics) {
    return <p className="text-center text-gray-400 py-12">Impossible de charger les analytics</p>;
  }

  const { daily_activity, category_spending, movement_types, stock_levels, low_stock_items, summary } = analytics;

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <Package className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-900">{summary.total_items}</p>
            <p className="text-xs text-gray-500">Articles</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-6 h-6 text-blue-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-900">{summary.total_movements_30d}</p>
            <p className="text-xs text-gray-500">Mouvements (30j)</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <ShoppingCart className="w-6 h-6 text-violet-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-900">{summary.total_spent_30d} EUR</p>
            <p className="text-xs text-gray-500">Depenses (30j)</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <AlertTriangle className={`w-6 h-6 mx-auto mb-1 ${summary.low_stock_count > 0 ? 'text-red-500' : 'text-green-500'}`} />
            <p className={`text-2xl font-bold ${summary.low_stock_count > 0 ? 'text-red-600' : 'text-green-600'}`}>{summary.low_stock_count}</p>
            <p className="text-xs text-gray-500">Stock bas</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Activity Chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <BarChart3 size={16} className="text-emerald-600" />
              Activite (7 derniers jours)
            </CardTitle>
            <div className="flex gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Achats</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-cyan-500" /> Consommations</span>
            </div>
          </CardHeader>
          <CardContent>
            <BarChartSimple data={daily_activity} dataKeyA="achats" dataKeyB="consommations" labelKey="label" height={180} />
          </CardContent>
        </Card>

        {/* Category Spending */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <PieChart size={16} className="text-violet-600" />
              Depenses par categorie (30j)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DonutChart data={category_spending} valueKey="total" labelKey="categorie" />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stock Levels */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Package size={16} className="text-emerald-600" />
              Niveaux de stock
              {low_stock_items.length > 0 && (
                <Badge variant="destructive" className="text-[10px] px-1.5 py-0">{low_stock_items.length} bas</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <HorizontalBars data={stock_levels} nameKey="nom" valueKey="stock" unitKey="unite" thresholdKey="seuil" />
          </CardContent>
        </Card>

        {/* Movement Types */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp size={16} className="text-blue-600" />
              Types de mouvements (30j)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 py-2">
              {Object.entries(movement_types).map(([type, count]) => {
                const total = Object.values(movement_types).reduce((s, v) => s + v, 0) || 1;
                const pct = (count / total) * 100;
                const colors = { ACHAT: 'bg-emerald-500', CONSOMMATION: 'bg-cyan-500', PERTE: 'bg-red-400' };
                const labels = { ACHAT: 'Achats', CONSOMMATION: 'Consommations', PERTE: 'Pertes' };
                return (
                  <div key={type}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-700 font-medium">{labels[type] || type}</span>
                      <span className="text-gray-500">{count} ({pct.toFixed(0)}%)</span>
                    </div>
                    <div className="bg-gray-100 rounded-full h-3 overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-700 ${colors[type] || 'bg-gray-400'}`} style={{ width: `${Math.max(pct, 1)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {low_stock_items.length > 0 && (
              <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg">
                <p className="text-xs font-medium text-red-700 mb-1">Articles en stock bas :</p>
                <div className="flex flex-wrap gap-1">
                  {low_stock_items.map((name, i) => (
                    <Badge key={i} variant="outline" className="text-[10px] text-red-600 border-red-200">{name}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
